import React, { useState, useEffect, useRef } from 'react';
import { useModal } from '../contexts/ModalContext';
import { motion, useAnimation, AnimatePresence } from 'framer-motion';
import styled from 'styled-components';
// Using proper relative imports
import DonationModal from '../components/DonationModal';
import DonationTracker from '../components/DonationTracker';

// Styled components for the page layout and elements
const Section = styled.section`
  padding: 5rem 0;
  position: relative;
  
  &.hero-section {
    background: linear-gradient(135deg, var(--primary) 0%, #1a472a 100%);
    color: white;
    padding-top: 7rem;
    padding-bottom: 7rem;
  }
  
  &.light-section {
    background-color: #f8f9fa;
  }
  
  &.white-section {
    background-color: white;
  }
`;

// Animated motion section for scroll animations
const MotionSection = styled(motion.section)`
  padding: 5rem 0;
  position: relative;
  overflow: hidden;
  
  &.hero-section {
    background: linear-gradient(135deg, var(--primary) 0%, #1a472a 100%);
    color: white;
    padding-top: 7rem;
    padding-bottom: 7rem;
  }
  
  &.light-section {
    background-color: #f8f9fa;
  }
  
  &.white-section {
    background-color: white;
  }
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  color: var(--primary);
  margin-bottom: 2.5rem;
  text-align: center;
  
  &.light {
    color: white;
  }
`;

const SectionContent = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 1.5rem;
`;

const ArabicText = styled.h1`
  font-size: 2.5rem;
  font-weight: 500;
  margin-bottom: 2rem;
  text-align: center;
  font-family: 'Amiri', 'Scheherazade New', serif;
  line-height: 1.5;
  
  /* Hide Arabic text on mobile devices */
  @media (max-width: 768px) {
    display: none;
  }
`;

const Paragraph = styled.p`
  font-size: 1.1rem;
  line-height: 1.8;
  margin-bottom: 1.5rem;
  color: inherit;
`;

const ButtonGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 1rem;
  margin-top: 2.5rem;
`;

const Button = styled.button`
  padding: 0.75rem 1.75rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 0.5rem;
  border: none;
  cursor: pointer;
  transition: all 0.3s ease;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  
  &.primary {
    background-color: var(--primary);
    color: white;
    
    &:hover {
      background-color: #0e5938;
      transform: translateY(-2px);
    }
  }
  
  &.accent {
    background-color: var(--accent);
    color: white;
    
    &:hover {
      background-color: #d05b00;
      transform: translateY(-2px);
    }
  }
  
  &.outline {
    background-color: transparent;
    color: var(--primary);
    border: 2px solid var(--primary);
    
    &:hover {
      background-color: var(--primary);
      color: white;
      transform: translateY(-2px);
    }
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 1rem;
  
  a {
    color: var(--primary);
    text-decoration: none;
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--accent);
    }
  }
`;

const FloatingButton = styled.button`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  width: 3.5rem;
  height: 3.5rem;
  border-radius: 50%;
  background-color: var(--accent);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  cursor: pointer;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.2);
  transition: all 0.3s ease;
  z-index: 100;
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 15px rgba(0, 0, 0, 0.3);
    background-color: #d05b00;
  }
  
  svg {
    width: 1.5rem;
    height: 1.5rem;
  }
`;

const EmailIcon = styled.svg`
  width: 1.5rem;
  height: 1.5rem;
  margin-right: 0.5rem;
  flex-shrink: 0;
`;

const FloatingButtonIcon = styled.svg`
  width: 1.5rem;
  height: 1.5rem;
`;

// Custom hook for scroll animations
const useElementOnScreen = (options: IntersectionObserverInit) => {
  const ref = useRef<HTMLDivElement>(null);
  const controls = useAnimation();

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          controls.start('visible');
        }
      });
    }, options);

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      if (ref.current) {
        observer.unobserve(ref.current);
      }
    };
  }, [controls, options]);

  return { ref, controls };
};

// Animated Section Component
interface AnimatedSectionProps {
  className?: string;
  id?: string;
  children: React.ReactNode;
  delay?: number;
  animation?: 'fadeUp' | 'fadeIn' | 'slideIn' | 'zoomIn';
}

const AnimatedSection: React.FC<AnimatedSectionProps> = ({
  children,
  className,
  id,
  delay = 0,
  animation = 'fadeUp'
}) => {
  const { ref, controls } = useElementOnScreen({
    threshold: 0.25,
    rootMargin: '-50px'
  });

  // Different animation variants
  const variants = {
    fadeUp: {
      hidden: { opacity: 0, y: 50 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.6,
          ease: 'easeOut',
          delay
        }
      }
    },
    fadeIn: {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          duration: 0.6,
          ease: 'easeOut',
          delay
        }
      }
    },
    slideIn: {
      hidden: { opacity: 0, x: -50 },
      visible: {
        opacity: 1,
        x: 0,
        transition: {
          duration: 0.6,
          ease: 'easeOut',
          delay
        }
      }
    },
    zoomIn: {
      hidden: { opacity: 0, scale: 0.9 },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          duration: 0.6,
          ease: 'easeOut',
          delay
        }
      }
    }
  };

  return (
    <MotionSection
      ref={ref}
      className={className}
      id={id}
      initial="hidden"
      animate={controls}
      variants={variants[animation]}
    >
      {children}
    </MotionSection>
  );
};

// Animated Component for child elements
interface AnimatedElementProps {
  children: React.ReactNode;
  delay?: number;
  className?: string;
  animation?: 'fadeUp' | 'fadeIn' | 'slideIn' | 'zoomIn';
}

const AnimatedElement: React.FC<AnimatedElementProps> = ({
  children,
  delay = 0,
  className = '',
  animation = 'fadeUp'
}) => {
  const { ref, controls } = useElementOnScreen({
    threshold: 0.25,
    rootMargin: '-50px'
  });

  // Animation variants
  const variants = {
    fadeUp: {
      hidden: { opacity: 0, y: 25 },
      visible: {
        opacity: 1,
        y: 0,
        transition: {
          duration: 0.5,
          ease: 'easeOut',
          delay
        }
      }
    },
    fadeIn: {
      hidden: { opacity: 0 },
      visible: {
        opacity: 1,
        transition: {
          duration: 0.5,
          ease: 'easeOut',
          delay
        }
      }
    },
    slideIn: {
      hidden: { opacity: 0, x: -25 },
      visible: {
        opacity: 1,
        x: 0,
        transition: {
          duration: 0.5,
          ease: 'easeOut',
          delay
        }
      }
    },
    zoomIn: {
      hidden: { opacity: 0, scale: 0.95 },
      visible: {
        opacity: 1,
        scale: 1,
        transition: {
          duration: 0.5,
          ease: 'easeOut',
          delay
        }
      }
    }
  };

  return (
    <motion.div
      ref={ref}
      className={className}
      initial="hidden"
      animate={controls}
      variants={variants[animation]}
    >
      {children}
    </motion.div>
  );
};

const HomePage: React.FC = () => {
  const { isModalOpen, setIsModalOpen } = useModal();
  const [isDashboardOpen, setIsDashboardOpen] = useState(false);
  const [hasShownInitialModal, setHasShownInitialModal] = useState(false);

  // Show donation modal automatically after a short delay when page loads
  useEffect(() => {
    if (!hasShownInitialModal) {
      const timer = setTimeout(() => {
        setIsModalOpen(true);
        setHasShownInitialModal(true);
      }, 3000); // Show after 3 seconds
      
      return () => clearTimeout(timer);
    }
  }, [hasShownInitialModal]);

  const handleModalOpen = () => {
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleDashboardOpen = () => {
    setIsDashboardOpen(true);
  };

  const handleDashboardClose = () => {
    setIsDashboardOpen(false);
  };

  return (
    <main>
      {/* Hero Section with Bismillah */}
      <AnimatedSection className="hero-section" animation="fadeIn">
        <SectionContent>
          <AnimatedElement delay={0.3} animation="fadeUp">
            <ArabicText>بسم الله الرحمن الرحيم</ArabicText>
          </AnimatedElement>

          <AnimatedElement delay={0.5} animation="fadeUp">
            <SectionTitle className="light">Welcome to Wisdom Youth Professional Wing</SectionTitle>
          </AnimatedElement>

          <AnimatedElement delay={0.7} animation="fadeUp">
            <Paragraph style={{ color: 'rgba(255, 255, 255, 0.9)', textAlign: 'center' }}>
              Our aim is to uphold the teachings of the Qur'an and Sunnah as comprehended by the Prophet(sa)
              and his companions(r), fostering a professional environment rooted in Islamic values.
            </Paragraph>
          </AnimatedElement>

        </SectionContent>
      </AnimatedSection>

      {/* About Section */}
      <AnimatedSection id="about" className="white-section" animation="fadeIn">
        <SectionContent style={{ maxWidth: '800px', margin: '0 auto' }}>
          <AnimatedElement delay={0.2} animation="zoomIn">
            <SectionTitle style={{
              marginBottom: '2rem',
              color: 'var(--primary)',
              fontSize: '2.5rem',
              textAlign: 'center',
              position: 'relative'
            }}>
              About Us
              <div style={{
                height: '4px',
                width: '60px',
                backgroundColor: 'var(--accent)',
                margin: '12px auto 0',
                borderRadius: '2px'
              }} />
            </SectionTitle>
          </AnimatedElement>

          <AnimatedElement delay={0.4} animation="fadeUp">
            <Paragraph style={{
              fontSize: '1.2rem',
              lineHeight: '1.8',
              marginBottom: '1.5rem',
              textAlign: 'center',
              fontWeight: '500',
              color: '#2d3748'
            }}>
              The Wisdom Youth Professional Wing is dedicated to fostering a community
              of skilled professionals who integrate Islamic values into their work and daily lives.
            </Paragraph>
          </AnimatedElement>

          <AnimatedElement delay={0.6} animation="fadeUp">
            <Paragraph style={{
              fontSize: '1.1rem',
              lineHeight: '1.7',
              marginBottom: '1.5rem',
              textAlign: 'center',
              color: '#4a5568'
            }}>
              We organize events, workshops, and training programs to help members develop their
              professional skills while staying connected to their Islamic identity.
            </Paragraph>
          </AnimatedElement>

          <AnimatedElement delay={0.8} animation="fadeUp">
            <Paragraph style={{
              fontSize: '1.1rem',
              lineHeight: '1.7',
              textAlign: 'center',
              color: '#4a5568',
              fontStyle: 'italic',
              borderLeft: '3px solid var(--primary)',
              paddingLeft: '20px',
              margin: '2rem auto',
              maxWidth: '90%'
            }}>
              Our initiatives are guided by the teachings of the Qur'an and Sunnah, focusing on
              integrity, excellence, and service to humanity.
            </Paragraph>
          </AnimatedElement>
        </SectionContent>
      </AnimatedSection>

      {/* Donations Section */}
      <AnimatedSection id="donations" className="light-section" animation="fadeIn">
        <SectionContent>
          <AnimatedElement delay={0.2} animation="zoomIn">
            <SectionTitle>Support Our Cause</SectionTitle>
          </AnimatedElement>

          <AnimatedElement delay={0.4} animation="fadeUp">
            <Paragraph style={{ textAlign: 'center' }}>
              Your contributions help us organize events, workshops, Islamic courses, and initiatives that benefit
              the community. Every donation, no matter how small, makes a significant impact.
            </Paragraph>
          </AnimatedElement>

          <AnimatedElement delay={0.6} animation="slideIn">
            <ButtonGroup>
              <Button
                className="primary"
                onClick={handleDashboardOpen}
              >
                Current Status
              </Button>

              <Button
                className="accent"
                onClick={handleModalOpen}
              >
                Support Now
              </Button>
            </ButtonGroup>
          </AnimatedElement>
        </SectionContent>
      </AnimatedSection>

      {/* Contact Section */}
      <AnimatedSection id="contact" className="white-section" animation="fadeIn">
        <SectionContent>
          <AnimatedElement delay={0.2} animation="zoomIn">
            <SectionTitle>Contact Us</SectionTitle>
          </AnimatedElement>

          <AnimatedElement delay={0.4} animation="fadeUp">
            <Paragraph style={{ textAlign: 'center' }}>
              Have questions or want to get involved? Reach out to us through our social media
              channels or drop us an email.
            </Paragraph>
          </AnimatedElement>

          <AnimatedElement delay={0.6} animation="zoomIn">
            <ContactItem style={{ justifyContent: 'center' }}>
              <a href="mailto:wisdomekm2020@gmail.com">
                <EmailIcon fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </EmailIcon>
                wisdomekm2020@gmail.com
              </a>
            </ContactItem>
          </AnimatedElement>
        </SectionContent>
      </AnimatedSection>

      {/* Floating Donation Button */}
      {!isModalOpen && (
        <AnimatePresence>
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.8, duration: 0.5 }}
          >
            <FloatingButton onClick={handleModalOpen} aria-label="Support Now">
              <FloatingButtonIcon fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </FloatingButtonIcon>
            </FloatingButton>
          </motion.div>
        </AnimatePresence>
      )}

      {/* Modal components */}
      {isModalOpen && <DonationModal onClose={handleModalClose} />}
      {isDashboardOpen && <DonationTracker onClose={handleDashboardClose} />}
    </main>
  );
};

export default HomePage;
